import type { Query, Filter } from "@material-table/core";
import type {
  FilterBuilderField,
  OrderingSchema,
  MTColumnSpech,
  FilterSchema,
  MTColumnsType,
  MTAdditionalColumnsMapping, DateRangeFilter,
} from "./types";

import { buildFilter } from "./filters";

function getDefaultValue<T extends object>(f: Filter<T>) {
  // is array
  if (Array.isArray(f.value)) {
    // for the case of partial matches on arrays
    return f.value.map((v: string) => `%${v}%`);
  }

  const kind = f.column?.type;
  switch (kind) {
    case "string":
      return `%${f.value}%`;
    case "boolean":
      return f.value === "checked";
    case "numeric":
      return parseFloat(f.value);
    case "date":
      // TODO check date in UTC
      return f.value.toISOString().split("T")[0];
    case "datetime":
      return f.value.toISOString();
    default:
      return f.value;
  }
}

function getDefaultOperator<T extends object>(f: Filter<T>) {
  if (Array.isArray(f.value)) {
    return "ilike any";
  }
  const kind = f.column?.type;
  switch (kind) {
    case "string":
      return "ilike";
    case "boolean":
      return "bool";
    default:
      return f.operator;
  }
}

function getDateRangeFilterOperator<T extends object>(value: DateRangeFilter) {
  const {startDate, endDate} = value;
  if (startDate && endDate) {
    return "between";
  }
  if (startDate && !endDate) {
    return ">=";
  }
  if (endDate && !startDate) {
    return "<=";
  }
  return null;
}

function addParenthesis(expression: string) {
  if (expression.includes(" ")) {
    return `(${expression})`;
  }
  return expression;
}

/**
 *
 * Converts the query object from material-table to a filter object
 * It support and additional column specification to override the default
 * column behaviour regarding filter operator and grouping
 *
 * @param query material-table query
 * @param spechLookup a lookup object for column specifications
 * @param additionalFields additional fields to be added to the filter
 */
export function filterBuilderFromQuery<T extends object>(
  query: Query<T>,
  spechLookup: { [key: string]: MTColumnSpech<T> } = {},
  additionalFields: FilterBuilderField[] = []
): { filterBuilder: FilterBuilderField[]; defaultexpression: string } {
  let defaultexpression = "";
  const filterBuilder = query.filters
    .reduce((acc, f) => {
      const spech = spechLookup[f.column.field as string];
      const columns = !!spech
        ? Array.isArray(spech.column_name)
          ? spech.column_name
          : [spech.column_name]
        : [f.column.field];
      const currentexpression = addParenthesis(columns.map((c) => c).join(" or "))
      defaultexpression = defaultexpression
        ? `${defaultexpression} and ${currentexpression}`
        : currentexpression;

      const generateFilter = (column: any, operator: string, value: any) => ({column, operator, value} as FilterBuilderField);
      const generateFilterValue = (value: any) => !!spech?.value_parser
        ? spech.value_parser(value)
        : getDefaultValue<T>(f);

      const operator = spech?.date_range_filter ? getDateRangeFilterOperator(f.value) : spech?.filter_operator || getDefaultOperator(f);
      
      let filters: FilterBuilderField[] = [];
      if (!!spech?.null_filter) {
        const {selected, nullFlag} = f.value;
        if (!!selected) {
          const value = generateFilterValue(selected);
          filters = [
            ...filters,
            ...columns.map((c) => generateFilter(c, operator, value))
          ];
        }
        if (nullFlag !== "all") {
          const nullOperator = nullFlag === "null" ? "empty" : "not_empty";
          filters = [
            ...filters,
            ...columns.map((c) => generateFilter(c, nullOperator, ""))
          ];
        }
      }
      else {
        if (spech?.date_range_filter) {
          const value = generateFilterValue(f.value);
          // if value is null, we want to remove the filter
          filters = value === null? [] : columns.map((c) => generateFilter(c, operator, value));
        } else {
          const value = generateFilterValue(f.value);
          filters = columns.map((c) => generateFilter(c, operator, value));
        }
      }

      return [...acc, ...filters];
    }, [] as FilterBuilderField[])
    .concat(additionalFields);
  defaultexpression = defaultexpression.trim();
  return { filterBuilder, defaultexpression };
}

function forceString(value: any) {
  if (typeof value === "string") {
    return value;
  }
  return JSON.stringify(value);
}

export function orderingFromQuery<T extends object>(
  query: Query<T>
): OrderingSchema | null {
  if (!query?.orderBy || !query?.orderBy?.field) {
    return null;
  }
  const order = {
    orderDirection: query.orderDirection,
    column: forceString(query.orderBy.field),
  };
  return { fields: [order] };
}

function filterEmptyBuilder(fbuilder: FilterBuilderField[]) {
  return fbuilder.filter((f) => {
    if (Array.isArray(f.value)) {
      return f.value.length > 0;
    }
    return f.value !== undefined && f.value !== null;
  });
}

export function buildFilterFromQuery<T extends object>(
  query: Query<T>,
  columns: MTColumnsType<T>,
  additionalColumnsMapping: MTAdditionalColumnsMapping<T> = {},
  expression = "",
  defaultLogic = "and"
): FilterSchema | null {
  const spechLookup = columns
    .filter((c) => !!c.pagination?.spech)
    .reduce(
      (acc, curr) => ({
        ...acc,
        [curr.field as string]: curr.pagination!.spech,
      }),
      {}
    ) as { [key: string]: MTColumnSpech<T> };

  const additional_columns = additionalColumnsMapping
    ? generateStringAdditionalColumns(query, additionalColumnsMapping)
    : [];
  const { filterBuilder, defaultexpression } = filterBuilderFromQuery(
    query,
    spechLookup,
    additional_columns
  );
  const filter = {
    expression: expression || defaultexpression,
    fields: filterEmptyBuilder(filterBuilder),
  };

  const filters = buildFilter(filter, defaultLogic);
  return filters;
}

export const generateStringAdditionalColumns = <T extends object>(
  query: Query<T>,
  mappings: MTAdditionalColumnsMapping<T>
): FilterBuilderField[] => {
  const filters = Object.entries(mappings)
    .map(([extraColumn, filterColumn]) => {
      // if is string
      if (typeof filterColumn.value === "string") {
        const filter = query.filters.find(
          (f: Filter<T>) => f.column.field === filterColumn.value
        );
        if (!filter) {
          return undefined;
        }
        const operator = filterColumn.operator ?? getDefaultOperator(filter);
        const value = getDefaultValue(filter!);
        const ret = {
          column: extraColumn,
          value: value,
          operator: operator,
        };
        return filterEmptyBuilder([ret])[0];
      }
      const value = filterColumn.value(query);
      const operator = filterColumn.operator;
      const ret = {
        column: extraColumn,
        value: value,
        operator: operator!,
      };
      return filterEmptyBuilder([ret])[0];
    })
    .filter((filter) => filter !== undefined) as FilterBuilderField[];
  return filters;
};
